import React, { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormField } from "@/components/stock/FormField";
import { selectCls } from "@/components/stock/LineItemsEditor";

export const ROLES = ["admin", "marketing", "purchasing", "viewer"];
const LEVELS = ["none", "view", "edit"];
const MODULE_LABEL = { dashboard: "dashboard", masters: "master_data", mappings: "mappings", stock: "inventory",
  shipments: "shipments", purchasing: "purchasing", reports: "reports", audit: "audit_log" };

function PermMatrix({ modules, perms, onChange, disabled }) {
  const { t } = useLang();
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="perm-matrix">
      <div className="grid grid-cols-[1fr_repeat(3,80px)] bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 px-3 py-2">
        <span>{t("page")}</span>{LEVELS.map((l) => <span key={l} className="text-center">{t(`perm_${l}`)}</span>)}
      </div>
      {modules.map((m) => (
        <div key={m} className="grid grid-cols-[1fr_repeat(3,80px)] items-center px-3 py-2 border-t border-slate-100 text-sm">
          <span>{t(MODULE_LABEL[m])}</span>
          {LEVELS.map((l) => (
            <label key={l} className="flex justify-center">
              <input type="radio" name={`perm-${m}`} checked={perms[m] === l} disabled={disabled}
                     onChange={() => onChange({ ...perms, [m]: l })} className="h-4 w-4 accent-blue-600" data-testid={`perm-${m}-${l}`} />
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

export function UserDialog({ open, initial, meta, onClose, onSave }) {
  const { t } = useLang();
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open) setF(initial ? { ...initial, password: "" } : { name: "", email: "", password: "", role: "viewer", active: true, permissions: meta.role_defaults.viewer });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const isAdminRole = f.role === "admin";
  const setRole = (role) => setF({ ...f, role, permissions: { ...meta.role_defaults[role] } });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle>{initial ? t("edit") : t("add")} · {t("account")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label={t("name")}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></FormField>
          <FormField label={t("email")}><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></FormField>
          {!initial && <FormField label={t("password")}><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></FormField>}
          <FormField label={t("role")}>
            <select className={selectCls} value={f.role} onChange={(e) => setRole(e.target.value)} data-testid="user-role-select">
              {ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
            </select>
          </FormField>
          <label className="flex items-center gap-3 text-sm sm:col-span-2">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" />
            {f.active ? t("active") : t("inactive")}
          </label>
        </div>
        <div className="space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800">{t("permissions")}</h4>
            {!isAdminRole && <Button size="sm" variant="ghost" onClick={() => setRole(f.role)} data-testid="perm-reset-default-btn">{t("use_role_default")}</Button>}
          </div>
          {isAdminRole && <p className="text-xs text-slate-500">{t("admin_full_access")}</p>}
          <PermMatrix modules={meta.modules} perms={isAdminRole ? meta.role_defaults.admin : f.permissions} disabled={isAdminRole}
                      onChange={(p) => setF({ ...f, permissions: p })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="user-cancel-btn">{t("cancel")}</Button>
          <Button onClick={() => onSave(f)} className="bg-slate-900 hover:bg-slate-800" data-testid="user-save-btn">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
